import { useQuery } from '@tanstack/react-query';

import { getKpiOptions } from '../api/ventura/contract';
import { EMPTY_KPI_OPTIONS, KpiOptions, normalizeKpiOptionsResponse } from '../util/kpiOptions';
import { useConfig } from './useConfig';

// useKpiOptions loads the shared KPI catalog (name → goal format) used to format
// KPI values. Any fetch failure is non-fatal — we fall back to EMPTY_KPI_OPTIONS
// (values then format as plain numbers; classification/color/Risk are unaffected).
// The common expected failure is a 403 for scoped users (the endpoint is
// manage-gated); network/5xx errors hit the same fallback.
function useKpiOptions() {
  const { apiHost } = useConfig();

  return useQuery<KpiOptions>({
    queryKey: ['kpiOptions', apiHost],
    queryFn: async () => {
      try {
        return normalizeKpiOptionsResponse(await getKpiOptions(apiHost));
      } catch (err) {
        // getRequestJSON doesn't expose the status, so we can't single out the
        // expected 403 — log every failure in dev so a genuine outage/misconfig
        // isn't hidden by the silent fallback.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('useKpiOptions: falling back to empty KPI catalog', err);
        }
        return EMPTY_KPI_OPTIONS;
      }
    },
    retry: false,
    staleTime: Infinity,
  });
}

export default useKpiOptions;
